import { useEffect, useState } from "react";
import { api, formatApiError } from "../../lib/api";

export default function AdminSettings() {
  const [form, setForm] = useState({ url: "", charityId: "", linkId: "" });
  const [state, setState] = useState({ status: "idle", message: "" });
  const [pw, setPw] = useState({ current: "", next: "", confirm: "" });
  const [pwState, setPwState] = useState({ status: "idle", message: "" });

  useEffect(() => {
    api.get("/admin/settings").then(({ data }) => setForm({
      url: data.settings.justgiving_url || "",
      charityId: data.settings.justgiving_charity_id || "",
      linkId: data.settings.justgiving_link_id || "",
    })).catch(() => {});
  }, []);

  const save = async (e) => {
    e.preventDefault();
    setState({ status: "loading", message: "" });
    try {
      await api.put("/admin/settings", { key: "justgiving_url", value: form.url });
      await api.put("/admin/settings", { key: "justgiving_charity_id", value: form.charityId });
      await api.put("/admin/settings", { key: "justgiving_link_id", value: form.linkId });
      setState({ status: "success", message: "Saved. The public Donate page now uses these details." });
    } catch (err) {
      setState({ status: "error", message: formatApiError(err) });
    }
  };

  const changePassword = async (e) => {
    e.preventDefault();
    if (pw.next !== pw.confirm) {
      setPwState({ status: "error", message: "The new passwords do not match." });
      return;
    }
    setPwState({ status: "loading", message: "" });
    try {
      await api.post("/auth/password/change", { current_password: pw.current, new_password: pw.next });
      setPwState({ status: "success", message: "Password changed. Any other signed-in sessions have been signed out." });
      setPw({ current: "", next: "", confirm: "" });
    } catch (err) {
      setPwState({ status: "error", message: formatApiError(err) });
    }
  };

  const inputCls = "w-full border border-sage bg-white px-4 py-2.5 text-sm rounded-sm focus:border-forest focus:ring-1 focus:ring-forest";

  return (
    <div data-testid="admin-settings">
      <h1 className="font-serif text-3xl font-semibold text-forest">Settings</h1>
      <p className="mt-2 text-sm text-charcoal/70 max-w-2xl leading-relaxed">
        Website configuration. Sensitive integration credentials are managed through deployment
        environment variables, never here.
      </p>

      <form onSubmit={save} className="mt-8 bg-white border border-sandstone p-6 lg:p-8 max-w-2xl" data-testid="settings-form">
        <h2 className="font-serif text-xl font-semibold text-forest">JustGiving</h2>
        <p className="mt-2 text-sm text-charcoal/70 leading-relaxed">
          Your official JustGiving page URL powers every Donate button on the public website.
          Adding the Giving Checkout identifiers below upgrades the Donate page to JustGiving's
          official on-site checkout popup, so donors give without leaving the site.
        </p>
        <label htmlFor="justgiving-url" className="block mt-5 text-xs tracking-wider uppercase font-semibold text-charcoal/70 mb-2">
          Page URL
        </label>
        <input id="justgiving-url" type="url" value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })}
          placeholder="https://www.justgiving.com/your-page"
          data-testid="justgiving-url-input"
          className="w-full border border-sage bg-white px-4 py-2.5 text-sm rounded-sm focus:border-forest focus:ring-1 focus:ring-forest" />
        <div className="grid sm:grid-cols-2 gap-4 mt-4">
          <div>
            <label htmlFor="justgiving-charity-id" className="block text-xs tracking-wider uppercase font-semibold text-charcoal/70 mb-2">
              Giving Checkout charity ID (optional)
            </label>
            <input id="justgiving-charity-id" value={form.charityId} onChange={(e) => setForm({ ...form, charityId: e.target.value })}
              placeholder="e.g. 2050"
              data-testid="justgiving-charity-id-input"
              className="w-full border border-sage bg-white px-4 py-2.5 text-sm rounded-sm focus:border-forest focus:ring-1 focus:ring-forest" />
          </div>
          <div>
            <label htmlFor="justgiving-link-id" className="block text-xs tracking-wider uppercase font-semibold text-charcoal/70 mb-2">
              Giving Checkout link ID (optional)
            </label>
            <input id="justgiving-link-id" value={form.linkId} onChange={(e) => setForm({ ...form, linkId: e.target.value })}
              placeholder="From your generated checkout code"
              data-testid="justgiving-link-id-input"
              className="w-full border border-sage bg-white px-4 py-2.5 text-sm rounded-sm focus:border-forest focus:ring-1 focus:ring-forest" />
          </div>
        </div>
        <p className="mt-3 text-xs text-charcoal/60 leading-relaxed">
          Find these in your JustGiving charity account under Giving Checkout → "add to your
          website": copy the <code>data-charityId</code> or <code>data-linkId</code> value from the
          code JustGiving generates. Copy them exactly — do not guess. Until they are added,
          donation buttons link to your JustGiving page. Until the page URL is saved, buttons
          remain in their clearly marked setup state.
        </p>
        <button type="submit" disabled={state.status === "loading"} data-testid="settings-save-button"
          className="mt-4 bg-forest text-white text-sm font-medium px-6 py-2.5 hover:bg-forest-dark transition-colors duration-300 disabled:opacity-60">
          {state.status === "loading" ? "Saving…" : "Save"}
        </button>
        {state.status === "success" && <p className="mt-3 text-sm text-forest" data-testid="settings-success">{state.message}</p>}
        {state.status === "error" && <p className="mt-3 text-sm text-red-700" role="alert" data-testid="settings-error">{state.message}</p>}
      </form>

      <form onSubmit={changePassword} className="mt-8 bg-white border border-sandstone p-6 lg:p-8 max-w-2xl" data-testid="password-change-form">
        <h2 className="font-serif text-xl font-semibold text-forest">Change password</h2>
        <p className="mt-2 text-sm text-charcoal/70 leading-relaxed">
          Applies to password-based accounts (your Super Administrator account). Changing the
          password signs out every other session. Your two-factor authentication stays unchanged.
        </p>
        <div className="mt-5 space-y-4 max-w-sm">
          <div>
            <label htmlFor="pw-current" className="block text-xs tracking-wider uppercase font-semibold text-charcoal/70 mb-2">Current password</label>
            <input id="pw-current" type="password" required autoComplete="current-password"
              value={pw.current} onChange={(e) => setPw({ ...pw, current: e.target.value })}
              className={inputCls} data-testid="pw-current-input" />
          </div>
          <div>
            <label htmlFor="pw-next" className="block text-xs tracking-wider uppercase font-semibold text-charcoal/70 mb-2">New password (min 10 characters)</label>
            <input id="pw-next" type="password" required minLength={10} autoComplete="new-password"
              value={pw.next} onChange={(e) => setPw({ ...pw, next: e.target.value })}
              className={inputCls} data-testid="pw-next-input" />
          </div>
          <div>
            <label htmlFor="pw-confirm" className="block text-xs tracking-wider uppercase font-semibold text-charcoal/70 mb-2">Confirm new password</label>
            <input id="pw-confirm" type="password" required minLength={10} autoComplete="new-password"
              value={pw.confirm} onChange={(e) => setPw({ ...pw, confirm: e.target.value })}
              className={inputCls} data-testid="pw-confirm-input" />
          </div>
        </div>
        <button type="submit" disabled={pwState.status === "loading"} data-testid="pw-change-submit"
          className="mt-4 bg-forest text-white text-sm font-medium px-6 py-2.5 hover:bg-forest-dark transition-colors duration-300 disabled:opacity-60">
          {pwState.status === "loading" ? "Changing…" : "Change password"}
        </button>
        {pwState.status === "success" && <p className="mt-3 text-sm text-forest" data-testid="pw-change-success">{pwState.message}</p>}
        {pwState.status === "error" && <p className="mt-3 text-sm text-red-700" role="alert" data-testid="pw-change-error">{pwState.message}</p>}
      </form>
    </div>
  );
}
